import React, { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import {
  ArrowLeft,
  Microphone,
  StopCircle,
  VideoCamera,
  PaperPlaneTilt,
  Spinner,
  LockKey,
  Phone,
  Copy,
  X,
  Trash,
  PaperPlane,
} from "@phosphor-icons/react";
import { api, API } from "../lib/api";
import useMediaRecorder from "../hooks/useMediaRecorder";

const POLL_MS = 4000;

function fmtTime(sec) {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function MediaPlayer({ fileUrl, kind, mine }) {
  // Falls back to a download link when the browser can't decode the blob
  // (typical case: WebM received on Safari / iOS). `preload="metadata"` +
  // `playsInline` are required for iOS Safari to show controls + play inline.
  const [failed, setFailed] = useState(false);

  if (failed) {
    return (
      <a
        href={fileUrl}
        download
        target="_blank"
        rel="noreferrer"
        data-testid={`thread-${kind}-download`}
        className={`inline-flex items-center gap-2 mt-1 px-3 py-2 rounded-lg text-sm underline ${
          mine ? "text-white bg-white/10 hover:bg-white/20" : "text-[#1E3A2B] bg-[#F3F0EA] hover:bg-[#E8E4DB]"
        }`}
      >
        {kind === "video" ? "Your browser can't play this clip — download" : "Download audio"}
      </a>
    );
  }

  if (kind === "audio") {
    return (
      <audio
        controls
        preload="metadata"
        src={fileUrl}
        onError={() => setFailed(true)}
        className="w-72 max-w-full mt-1"
        data-testid="thread-audio-player"
      />
    );
  }
  return (
    <video
      controls
      playsInline
      preload="metadata"
      src={fileUrl}
      onError={() => setFailed(true)}
      className="w-80 max-w-full rounded-lg mt-1 bg-black"
      data-testid="thread-video-player"
    />
  );
}

function MessageBubble({ msg, mine, fileUrl, canDelete, onDelete }) {
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"} group`}>
      {mine && canDelete && (
        <button
          onClick={() => onDelete(msg)}
          data-testid={`thread-msg-delete-${msg.id}`}
          aria-label="Delete message"
          title="Delete this message"
          className="self-center mr-2 opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded-full text-[#8A948E] hover:text-[#C77D5E] hover:bg-[#F3F0EA]"
        >
          <Trash size={14} weight="duotone" />
        </button>
      )}
      <div
        data-testid={`thread-msg-${msg.kind}`}
        className={`max-w-[85%] rounded-2xl px-4 py-3 ${
          mine
            ? "bg-[#1E3A2B] text-white rounded-br-sm"
            : "bg-white border border-[#E8E4DB] rounded-bl-sm"
        }`}
      >
        <div
          className={`text-[10px] uppercase tracking-[0.15em] mb-1 ${
            mine ? "text-[#A8C0B4]" : "text-[#768C7E]"
          }`}
        >
          {msg.sender === "user" ? "You" : "Coach"} · {new Date(msg.created_at).toLocaleTimeString()}
        </div>
        {msg.kind === "text" && (
          <div className="whitespace-pre-wrap leading-relaxed">{msg.text}</div>
        )}
        {msg.kind === "audio" && fileUrl && (
          <MediaPlayer fileUrl={fileUrl} kind="audio" mine={mine} />
        )}
        {msg.kind === "video" && fileUrl && (
          <MediaPlayer fileUrl={fileUrl} kind="video" mine={mine} />
        )}
        {msg.duration_sec && (
          <div
            className={`text-[10px] mt-1 ${
              mine ? "text-[#A8C0B4]" : "text-[#8A948E]"
            }`}
          >
            {fmtTime(Math.round(msg.duration_sec))}
          </div>
        )}
      </div>
      {!mine && canDelete && (
        <button
          onClick={() => onDelete(msg)}
          data-testid={`thread-msg-delete-${msg.id}`}
          aria-label="Delete message"
          title="Delete this message"
          className="self-center ml-2 opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded-full text-[#8A948E] hover:text-[#C77D5E] hover:bg-[#F3F0EA]"
        >
          <Trash size={14} weight="duotone" />
        </button>
      )}
    </div>
  );
}

function VoiceRecorderPanel({ onSend, onCancel, recipientLabel = "coach" }) {
  const { recording, elapsed, start, stop } = useMediaRecorder({ video: false });
  const [blob, setBlob] = useState(null);
  const [duration, setDuration] = useState(0);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    start();
    // eslint-disable-next-line
  }, []);

  const handleStop = async () => {
    setDuration(elapsed);
    const b = await stop();
    setBlob(b);
  };

  const send = async () => {
    if (!blob) return;
    setSending(true);
    try {
      await onSend(blob, duration);
    } finally {
      setSending(false);
    }
  };

  const audioUrl = blob ? URL.createObjectURL(blob) : null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-40 bg-white border-t border-[#E8E4DB] shadow-2xl p-5 sm:p-6">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-3">
          <div className="font-serif text-lg text-[#1E3A2B]">
            {recording ? "Recording…" : blob ? "Review your message" : "Preparing…"}
          </div>
          <button onClick={onCancel} className="text-[#8A948E] hover:text-[#1E3A2B]" aria-label="Close">
            <X size={20} weight="bold" />
          </button>
        </div>
        {recording ? (
          <div className="flex items-center justify-between gap-4 bg-[#F3F0EA] rounded-xl p-4">
            <div className="flex items-center gap-3">
              <span className="w-3 h-3 rounded-full bg-[#C77D5E] animate-pulse" />
              <span className="font-mono text-[#1E3A2B] text-lg">{fmtTime(elapsed)}</span>
            </div>
            <button
              onClick={handleStop}
              data-testid="voice-stop-button"
              className="inline-flex items-center gap-2 bg-[#1E3A2B] text-white px-5 py-2.5 rounded-full hover:bg-[#2C4C3B]"
            >
              <StopCircle size={18} weight="fill" /> Stop
            </button>
          </div>
        ) : blob ? (
          <div>
            <audio controls src={audioUrl} className="w-full" />
            <div className="mt-4 flex flex-wrap gap-2 justify-end">
              <button
                onClick={onCancel}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#F3F0EA] hover:bg-[#E8E4DB] text-[#121815]"
              >
                <Trash size={16} weight="duotone" /> Discard
              </button>
              <button
                onClick={send}
                disabled={sending}
                data-testid="voice-send-button"
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-[#1E3A2B] text-white hover:bg-[#2C4C3B] disabled:opacity-60"
              >
                {sending ? <Spinner size={16} className="animate-spin" /> : <PaperPlane size={16} weight="fill" />}
                Send to {recipientLabel}
              </button>
            </div>
          </div>
        ) : (
          <div className="text-sm text-[#8A948E]">Allow microphone access to begin.</div>
        )}
      </div>
    </div>
  );
}

function VideoRecorderPanel({ onSend, onCancel, recipientLabel = "coach" }) {
  const { recording, elapsed, stream, start, stop } = useMediaRecorder({ video: true });
  const [blob, setBlob] = useState(null);
  const [duration, setDuration] = useState(0);
  const [sending, setSending] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    start();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
    }
  }, [stream]);

  const handleStop = async () => {
    setDuration(elapsed);
    const b = await stop();
    setBlob(b);
  };

  const send = async () => {
    if (!blob) return;
    setSending(true);
    try {
      await onSend(blob, duration);
    } finally {
      setSending(false);
    }
  };

  const videoUrl = blob ? URL.createObjectURL(blob) : null;

  return (
    <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full overflow-hidden shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E8E4DB]">
          <div className="font-serif text-lg text-[#1E3A2B]">
            {recording ? "Recording video…" : blob ? "Review video" : "Preparing camera…"}
          </div>
          <button onClick={onCancel} className="text-[#8A948E] hover:text-[#1E3A2B]" aria-label="Close">
            <X size={20} weight="bold" />
          </button>
        </div>
        <div className="p-5">
          {!blob ? (
            <video
              ref={videoRef}
              autoPlay
              muted
              playsInline
              className="w-full rounded-xl bg-black aspect-video"
              data-testid="video-recorder-preview"
            />
          ) : (
            <video
              src={videoUrl}
              controls
              className="w-full rounded-xl bg-black aspect-video"
              data-testid="video-recorder-playback"
            />
          )}
          <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
            {recording ? (
              <>
                <div className="flex items-center gap-2 text-[#C77D5E]">
                  <span className="w-2.5 h-2.5 rounded-full bg-[#C77D5E] animate-pulse" />
                  <span className="font-mono text-[#1E3A2B]">{fmtTime(elapsed)}</span>
                </div>
                <button
                  onClick={handleStop}
                  data-testid="video-stop-button"
                  className="inline-flex items-center gap-2 bg-[#1E3A2B] text-white px-5 py-2.5 rounded-full hover:bg-[#2C4C3B]"
                >
                  <StopCircle size={18} weight="fill" /> Stop
                </button>
              </>
            ) : blob ? (
              <div className="ml-auto flex flex-wrap gap-2">
                <button
                  onClick={onCancel}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#F3F0EA] hover:bg-[#E8E4DB] text-[#121815]"
                >
                  <Trash size={16} weight="duotone" /> Discard
                </button>
                <button
                  onClick={send}
                  disabled={sending}
                  data-testid="video-send-button"
                  className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-[#1E3A2B] text-white hover:bg-[#2C4C3B] disabled:opacity-60"
                >
                  {sending ? <Spinner size={16} className="animate-spin" /> : <PaperPlane size={16} weight="fill" />}
                  Send to {recipientLabel}
                </button>
              </div>
            ) : (
              <div className="text-sm text-[#8A948E]">Allow camera access to begin.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function LiveRoomPanel({ threadId, onClose }) {
  // Use Jitsi Meet (no API key required) — both parties join the same room
  const room = `conflict-coach-${threadId}`;
  const url = `https://meet.jit.si/${room}#config.prejoinPageEnabled=true`;
  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col">
      <div className="flex items-center justify-between px-5 py-3 bg-[#121815] text-white">
        <div className="flex items-center gap-2">
          <Phone size={18} weight="duotone" />
          <span className="font-serif text-lg">Live conversation</span>
          <span className="text-xs text-[#8A948E] ml-3">Room: {room}</span>
        </div>
        <button onClick={onClose} className="px-4 py-2 rounded-full bg-[#2C4C3B] hover:bg-[#1E3A2B]">
          End
        </button>
      </div>
      <iframe
        src={url}
        title="Live conversation"
        allow="camera; microphone; fullscreen; speaker; display-capture; autoplay"
        className="flex-1 w-full border-0"
      />
    </div>
  );
}

export default function CoachThread() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { coach, leader } = useAuth();
  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [voicePanel, setVoicePanel] = useState(false);
  const [videoPanel, setVideoPanel] = useState(false);
  const [livePanel, setLivePanel] = useState(false);
  const [senderRole, setSenderRole] = useState("user"); // user | coach
  const [linkCopied, setLinkCopied] = useState(false);
  const lastTsRef = useRef(null);
  const bottomRef = useRef(null);

  // Auto-switch sender role based on who's signed in (coach login wins).
  // Falls back to the per-browser preference if neither is signed in.
  useEffect(() => {
    if (coach) {
      setSenderRole("coach");
      return;
    }
    if (leader) {
      setSenderRole("user");
      return;
    }
    const saved = localStorage.getItem("ccfl-sender-role");
    if (saved === "coach") setSenderRole("coach");
  }, [coach, leader]);

  useEffect(() => {
    localStorage.setItem("ccfl-sender-role", senderRole);
  }, [senderRole]);

  // mark thread seen for whoever opens it
  useEffect(() => {
    if (!id) return;
    if (coach) {
      api.post(`/coach/dashboard/threads/${id}/seen`).catch(() => {});
    } else if (leader) {
      api.post(`/leader/threads/${id}/seen`).catch(() => {});
    }
  }, [id, coach, leader, messages.length]);

  // initial load
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await api.get(`/coach/thread/${id}`);
        if (!active) return;
        setThread(data.thread);
        setMessages(data.messages);
        if (data.messages.length) {
          lastTsRef.current = data.messages[data.messages.length - 1].created_at;
        }
      } catch (e) {
        toast.error("Could not load thread");
      }
    })();
    return () => {
      active = false;
    };
  }, [id]);

  // polling for new messages
  useEffect(() => {
    const tick = async () => {
      try {
        const params2 = lastTsRef.current ? `?since=${encodeURIComponent(lastTsRef.current)}` : "";
        const { data } = await api.get(`/coach/thread/${id}/messages${params2}`);
        if (data.messages?.length) {
          setMessages((prev) => [...prev, ...data.messages]);
          lastTsRef.current = data.messages[data.messages.length - 1].created_at;
        }
      } catch (_) {
        /* silent */
      }
    };
    const handle = setInterval(tick, POLL_MS);
    return () => clearInterval(handle);
  }, [id]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length, voicePanel, videoPanel]);

  const fileUrlFor = (m) => {
    if (!m.storage_path) return null;
    // The <video>/<audio> element can't attach headers, so the access secret rides
    // as a query param on this one endpoint. Coaches signed in via JWT don't need it.
    const secrets = (() => {
      try {
        return JSON.parse(localStorage.getItem("ccfl_thread_secrets") || "{}");
      } catch {
        return {};
      }
    })();
    const sec = secrets[id];
    const base = `${API}/coach/thread/${id}/file?path=${encodeURIComponent(m.storage_path)}`;
    return sec ? `${base}&s=${encodeURIComponent(sec)}` : base;
  };

  // Coaches signed in (admin or assigned coach) can delete individual messages
  const canDeleteMessages = !!coach;
  const handleDeleteMessage = async (m) => {
    if (!window.confirm("Delete this message? This cannot be undone.")) return;
    try {
      await api.delete(`/coach/dashboard/threads/${id}/messages/${m.id}`);
      setMessages((prev) => prev.filter((x) => x.id !== m.id));
      toast.success("Message deleted.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete.");
    }
  };

  const sendText = async (e) => {
    e?.preventDefault();
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("sender", senderRole);
      fd.append("text", text.trim());
      const { data } = await api.post(`/coach/thread/${id}/message`, fd);
      setMessages((m) => [...m, data]);
      lastTsRef.current = data.created_at;
      setText("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const sendMedia = async (blob, durationSec) => {
    const isVideo = blob.type.startsWith("video/");
    const ext = isVideo
      ? blob.type.includes("mp4")
        ? "mp4"
        : "webm"
      : blob.type.includes("mp4")
      ? "m4a"
      : "webm";
    const fd = new FormData();
    fd.append("sender", senderRole);
    fd.append("duration_sec", String(durationSec || 0));
    fd.append("file", blob, `recording.${ext}`);
    try {
      const { data } = await api.post(`/coach/thread/${id}/message`, fd);
      setMessages((m) => [...m, data]);
      lastTsRef.current = data.created_at;
      setVoicePanel(false);
      setVideoPanel(false);
      toast.success("Sent.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.origin + window.location.pathname);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
      toast.success("Thread link copied");
    } catch {
      toast.error("Could not copy link");
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 sm:px-8 py-8 lg:py-12 pb-44">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button
          onClick={() => nav("/coach")}
          data-testid="thread-back-link"
          className="inline-flex items-center gap-1.5 text-sm text-[#4A524C] hover:text-[#1E3A2B]"
        >
          <ArrowLeft size={16} weight="bold" /> Back
        </button>
        <div className="flex items-center gap-3 flex-wrap">
          <select
            value={senderRole}
            onChange={(e) => setSenderRole(e.target.value)}
            data-testid="thread-sender-select"
            className="text-xs bg-white border border-[#E8E4DB] rounded-full px-3 py-1.5 text-[#4A524C]"
            title="Posting as"
          >
            <option value="user">Posting as: You (leader)</option>
            <option value="coach">Posting as: Coach</option>
          </select>
          <button
            onClick={copyLink}
            data-testid="thread-copy-link"
            className="inline-flex items-center gap-1.5 text-xs text-[#4A524C] hover:text-[#1E3A2B]"
          >
            <Copy size={14} weight="duotone" /> {linkCopied ? "Copied" : "Copy thread link"}
          </button>
        </div>
      </div>

      <div className="mt-4">
        <div className="label-kicker">Concierge thread</div>
        <h1 className="font-serif text-3xl sm:text-4xl text-[#1E3A2B] tracking-tight mt-2">
          You and your coach.
        </h1>
        <p className="text-xs text-[#8A948E] mt-2 inline-flex items-center gap-1.5">
          <LockKey size={12} weight="duotone" /> Private thread — only you and your coach can read or post.
        </p>
      </div>

      {/* Scheduled appointment banner (only shown when there's a scheduled call) */}
      {thread?.scheduled_at && (() => {
        const when = new Date(thread.scheduled_at);
        const now = new Date();
        const minutesAway = Math.round((when.getTime() - now.getTime()) / 60000);
        const isOpen = minutesAway <= 5; // can join 5 min early
        const isPast = minutesAway < -180; // 3 hours past
        const label = thread.scheduled_medium === "video" ? "Video conversation" : "Audio conversation";
        return (
          <div className="mt-6 bg-[#F3F0EA] border border-[#E8E4DB] rounded-2xl p-5 flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="label-kicker">{label} scheduled</div>
              <div className="font-serif text-lg text-[#1E3A2B] mt-1">
                {when.toLocaleString([], {
                  weekday: "short",
                  month: "short",
                  day: "numeric",
                  hour: "numeric",
                  minute: "2-digit",
                })}
              </div>
              <div className="text-xs text-[#4A524C] mt-1">
                {isPast
                  ? "This call has passed — you can still send messages on this thread."
                  : isOpen
                  ? "The room is open. Click to join."
                  : minutesAway > 60
                  ? `Opens in ${Math.round(minutesAway / 60)} hour${minutesAway >= 120 ? "s" : ""}`
                  : `Opens in ${minutesAway} minute${minutesAway === 1 ? "" : "s"}`}
              </div>
            </div>
            <button
              onClick={() => setLivePanel(true)}
              disabled={!isOpen}
              data-testid="thread-live-button"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#1E3A2B] text-white hover:bg-[#2C4C3B] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Phone size={16} weight="duotone" /> {isOpen ? "Open live room" : "Not yet open"}
            </button>
          </div>
        );
      })()}

      {/* Messages */}
      <div className="mt-6 space-y-3" data-testid="thread-messages">
        {messages.length === 0 && (
          <div className="text-center py-12 text-sm text-[#8A948E]">
            No messages yet. Send a voice note, video, or text to get started.
          </div>
        )}
        {messages.map((m) => (
          <MessageBubble
            key={m.id}
            msg={m}
            mine={m.sender === senderRole}
            fileUrl={fileUrlFor(m)}
            canDelete={canDeleteMessages}
            onDelete={handleDeleteMessage}
          />
        ))}
        <div ref={bottomRef} />
      </div>

      {/* Composer */}
      <form
        onSubmit={sendText}
        className="fixed bottom-4 left-0 right-0 mx-auto max-w-3xl px-6 sm:px-8 z-30"
      >
        <div className="bg-white border border-[#E8E4DB] rounded-2xl p-3 flex items-end gap-2 shadow-lg">
          <textarea
            data-testid="thread-text-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={1}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                sendText();
              }
            }}
            placeholder="Type a message…"
            className="flex-1 resize-none bg-transparent focus:outline-none p-2 max-h-40 text-[#121815] placeholder-[#8A948E]"
          />
          <button
            type="button"
            onClick={() => setVoicePanel(true)}
            data-testid="thread-voice-button"
            className="w-10 h-10 rounded-full bg-[#F3F0EA] text-[#1E3A2B] hover:bg-[#E8E4DB] flex items-center justify-center"
            title="Record voice note"
          >
            <Microphone size={18} weight="fill" />
          </button>
          <button
            type="button"
            onClick={() => setVideoPanel(true)}
            data-testid="thread-video-button"
            className="w-10 h-10 rounded-full bg-[#F3F0EA] text-[#1E3A2B] hover:bg-[#E8E4DB] flex items-center justify-center"
            title="Record video message"
          >
            <VideoCamera size={18} weight="fill" />
          </button>
          <button
            type="submit"
            disabled={sending || !text.trim()}
            data-testid="thread-text-send-button"
            className="w-10 h-10 rounded-full bg-[#1E3A2B] text-white hover:bg-[#2C4C3B] disabled:opacity-50 flex items-center justify-center"
            title="Send text"
          >
            <PaperPlaneTilt size={18} weight="fill" />
          </button>
        </div>
      </form>

      {voicePanel && (
        <VoiceRecorderPanel
          onSend={sendMedia}
          onCancel={() => setVoicePanel(false)}
          recipientLabel={senderRole === "coach" ? "leader" : "coach"}
        />
      )}
      {videoPanel && (
        <VideoRecorderPanel
          onSend={sendMedia}
          onCancel={() => setVideoPanel(false)}
          recipientLabel={senderRole === "coach" ? "leader" : "coach"}
        />
      )}
      {livePanel && <LiveRoomPanel threadId={id} onClose={() => setLivePanel(false)} />}
    </div>
  );
}
